import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert, Box, Button, Chip, CircularProgress, FormControl, InputLabel, MenuItem, Paper, Select,
    SelectChangeEvent, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { AuthService } from '../../../services/auth.service';
import { useToast } from '../../../context/ToastContext';
import { CommercialReportService } from '../../../services/commercialReport.service';
import { DUE_STATUS_COLORS, ILcPositionReport, ILcPositionSubtotal, IMismatchedCurrencyAmount, LcStatus, REPORT_GRAND_TOTAL_KEY } from '../../../interfaces/commercial.interface';
import { dash, money, triggerDownload } from '../../statutoryRegisters/shared';
import { enumLabel } from '../../../utils/garmentsLabels';
import { toLocalIsoDate } from '../../../utils/dateUtils';

const LC_STATUSES: LcStatus[] = ['RECEIVED', 'PARTIALLY_SHIPPED', 'FULLY_SHIPPED', 'REALIZED', 'CLOSED'];

/** Distinct currency codes present across a set of subtotal rows, in first-seen order. */
const distinctCurrencies = (subtotals: ILcPositionSubtotal[]): string[] => {
    const seen: string[] = [];
    subtotals.forEach(s => { if (!seen.includes(s.currencyCode)) seen.push(s.currencyCode); });
    return seen;
};

const SubtotalTable: React.FC<{ title: string; rows: ILcPositionSubtotal[]; currency: string; testIdPrefix: string }> = (
    { title, rows, currency, testIdPrefix }
) => (
    <TableContainer data-help-section="commercialReports.lcPosition.totals" component={Paper} sx={{ mb: 2 }} data-testid={`${testIdPrefix}-${currency}`}>
        <Typography variant="subtitle2" sx={{ p: 1 }}>{title} — {currency}</Typography>
        <Table size="small">
            <TableHead>
                <TableRow>
                    <TableCell data-help="commercialReports.lcPosition.col.group">{title === 'Grand Total' ? 'Currency' : 'Bank'}</TableCell>
                    <TableCell data-help="commercialReports.lcPosition.col.lcCount" align="right">LC Count</TableCell>
                    <TableCell data-help="commercialReports.lcPosition.col.lcAmount" align="right">LC Amount</TableCell>
                    <TableCell data-help="commercialReports.lcPosition.col.cap" align="right">Entitlement Cap</TableCell>
                    <TableCell data-help="commercialReports.lcPosition.col.b2bOpened" align="right">B2B Opened</TableCell>
                    <TableCell data-help="commercialReports.lcPosition.col.b2bUtilized" align="right">B2B Utilized</TableCell>
                    <TableCell data-help="commercialReports.lcPosition.col.headroom" align="right">Headroom</TableCell>
                    <TableCell data-help="commercialReports.lcPosition.col.expDeclared" align="right">EXP Declared</TableCell>
                    <TableCell data-help="commercialReports.lcPosition.col.expRealized" align="right">EXP Realized</TableCell>
                    <TableCell data-help="commercialReports.lcPosition.col.expUnrealized" align="right">EXP Unrealized</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {rows.map((s, i) => (
                    <TableRow key={`${s.groupKey}-${s.currencyCode}-${i}`}>
                        <TableCell>{s.groupKey === REPORT_GRAND_TOTAL_KEY ? s.currencyCode : s.groupKey}</TableCell>
                        <TableCell align="right">{s.lcCount}</TableCell>
                        <TableCell align="right">{money(s.lcAmount)}</TableCell>
                        <TableCell align="right">{money(s.entitlementCap)}</TableCell>
                        <TableCell align="right">{money(s.b2bOpened)}</TableCell>
                        <TableCell align="right">{money(s.b2bUtilized)}</TableCell>
                        <TableCell align="right">{money(s.headroom)}</TableCell>
                        <TableCell align="right">{money(s.expDeclared)}</TableCell>
                        <TableCell align="right">{money(s.expRealized)}</TableCell>
                        <TableCell align="right">{money(s.expUnrealized)}</TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    </TableContainer>
);

/**
 * M-3: per-currency, joined "amount currency" rendering matching
 * `CommercialXlsxService.formatMismatchedCurrencyAmounts` -- never summed across currencies, and
 * never silently dropped (the earlier screen bug this fix closes).
 */
const formatMismatchedCurrencyAmounts = (amounts: IMismatchedCurrencyAmount[]): string =>
    amounts.map(m => `${money(m.amount)} ${m.currencyCode}`).join('; ');

/**
 * T11 report 1 — LC position (T8 brief item 1). R-2 as extended: `byBank`/`grandTotal` are keyed
 * (groupKey, currencyCode) — a mixed-currency institution renders one sub-total BLOCK PER CURRENCY
 * at every level, never a single combined figure. Filters below are client-side only — the T8
 * report endpoints take no query parameters (whole-institution snapshot).
 */
const LcPositionReport: React.FC = () => {
    const toast = useToast();
    const institutionId = AuthService.getSelectedInstitution()!;
    const [report, setReport] = useState<ILcPositionReport | null>(null);
    const [loading, setLoading] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [statusFilter, setStatusFilter] = useState<LcStatus | 'ALL'>('ALL');

    const load = async () => {
        setLoading(true);
        try {
            const data = await CommercialReportService.getLcPositionReport(institutionId);
            setReport(data);
        } catch (e) {
            setReport(null);
            toast.showApiError(e, 'Failed to load LC position report');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [institutionId]);

    const handleDownload = async () => {
        setDownloading(true);
        try {
            const blob = await CommercialReportService.downloadLcPositionReportXlsx(institutionId);
            triggerDownload(blob, `lc-position-${toLocalIsoDate()}.xlsx`);
        } catch (e) {
            toast.showApiError(e, 'Download failed');
        } finally {
            setDownloading(false);
        }
    };

    const filteredRows = useMemo(
        () => (report?.rows ?? []).filter(r => statusFilter === 'ALL' || r.status === statusFilter),
        [report, statusFilter]
    );

    const grandTotalCurrencies = useMemo(() => distinctCurrencies(report?.grandTotal ?? []), [report]);
    const byBankCurrencies = useMemo(() => distinctCurrencies(report?.byBank ?? []), [report]);

    return (
        <Box data-help-page="commercialReports.lcPosition.page">
            <Box data-help-section="commercialReports.lcPosition.toolbar" display="flex" gap={2} alignItems="flex-end" mb={2} flexWrap="wrap">
                <Button data-help="commercialReports.lcPosition.refresh" variant="outlined" size="small" onClick={load} disabled={loading}>Refresh</Button>
                <Button data-help="commercialReports.lcPosition.download"
                    variant="contained" size="small" startIcon={<FileDownloadIcon />}
                    onClick={handleDownload} disabled={!report || downloading}
                >
                    Download XLSX
                </Button>
                <FormControl data-help="commercialReports.lcPosition.status" size="small" sx={{ flex: '1 1 180px', maxWidth: 300 }}>
                    <InputLabel id="lc-position-status-filter-label">Status</InputLabel>
                    <Select
                        labelId="lc-position-status-filter-label"
                        label="Status"
                        value={statusFilter}
                        onChange={(e: SelectChangeEvent) => setStatusFilter(e.target.value as LcStatus | 'ALL')}
                    >
                        <MenuItem value="ALL">All Statuses</MenuItem>
                        {LC_STATUSES.map(s => <MenuItem key={s} value={s}>{enumLabel(s)}</MenuItem>)}
                    </Select>
                </FormControl>
            </Box>

            {loading && <CircularProgress size={24} />}

            {!loading && report && (
                <>
                    {report.truncated && (
                        <Alert severity="warning" sx={{ mb: 2 }} data-testid="lc-position-truncated">
                            Showing {report.rows.length} of {report.totalMatchingRows} matching LCs. Totals below
                            still reflect the full, untruncated dataset.
                        </Alert>
                    )}

                    <Typography variant="h6" sx={{ mb: 1 }}>Grand Total</Typography>
                    {grandTotalCurrencies.length === 0 && (
                        <Typography color="text.secondary" sx={{ mb: 2 }}>No data.</Typography>
                    )}
                    {grandTotalCurrencies.map(currency => (
                        <SubtotalTable
                            key={`grand-${currency}`}
                            title="Grand Total"
                            currency={currency}
                            testIdPrefix="lc-position-grand-total"
                            rows={(report.grandTotal ?? []).filter(s => s.currencyCode === currency)}
                        />
                    ))}

                    <Typography variant="h6" sx={{ mb: 1, mt: 2 }}>By Bank</Typography>
                    {byBankCurrencies.length === 0 && (
                        <Typography color="text.secondary" sx={{ mb: 2 }}>No data.</Typography>
                    )}
                    {byBankCurrencies.map(currency => (
                        <SubtotalTable
                            key={`bank-${currency}`}
                            title="By Bank"
                            currency={currency}
                            testIdPrefix="lc-position-by-bank"
                            rows={(report.byBank ?? []).filter(s => s.currencyCode === currency)}
                        />
                    ))}

                    <Typography variant="h6" sx={{ mb: 1, mt: 2 }}>LCs</Typography>
                    <TableContainer data-help-section="commercialReports.lcPosition.table" component={Paper} data-testid="lc-position-rows-table">
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell data-help="commercialReports.lcPosition.col.lcNumber">LC Number</TableCell>
                                    <TableCell data-help="commercialReports.lcPosition.col.bank">Bank</TableCell>
                                    <TableCell data-help="commercialReports.lcPosition.col.buyer">Buyer</TableCell>
                                    <TableCell data-help="commercialReports.lcPosition.col.currency">Currency</TableCell>
                                    <TableCell data-help="commercialReports.lcPosition.col.lcAmount" align="right">LC Amount</TableCell>
                                    <TableCell data-help="commercialReports.lcPosition.col.headroom" align="right">Headroom</TableCell>
                                    <TableCell data-help="commercialReports.lcPosition.col.expUnrealized" align="right">EXP Unrealized</TableCell>
                                    <TableCell data-help="commercialReports.lcPosition.col.status">Status</TableCell>
                                    <TableCell data-help="commercialReports.lcPosition.col.due">Due</TableCell>
                                    <TableCell data-help="commercialReports.lcPosition.col.mismatch">Mismatched Currency</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredRows.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={10} align="center">
                                            <Typography color="text.secondary">No LCs match the current filter.</Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                                {filteredRows.map(row => (
                                    <TableRow key={row.lcId} hover>
                                        <TableCell>{row.lcNumber}</TableCell>
                                        <TableCell>{row.issuingBank}</TableCell>
                                        <TableCell>{row.buyerName}</TableCell>
                                        <TableCell>{row.currencyCode}</TableCell>
                                        <TableCell align="right">{money(row.lcAmount)}</TableCell>
                                        <TableCell align="right">{money(row.headroom)}</TableCell>
                                        <TableCell align="right">
                                            {money(row.expUnrealized)}
                                            {row.unrealizedAgainstClosed && (
                                                <Chip size="small" sx={{ ml: 1 }} color="warning" label="dangling" />
                                            )}
                                        </TableCell>
                                        <TableCell>{enumLabel(row.status)}</TableCell>
                                        <TableCell>
                                            <Chip size="small" label={dash(row.dueReason) === '—' ? enumLabel(row.dueStatus) : `${enumLabel(row.dueStatus)} (${row.dueReason})`} color={DUE_STATUS_COLORS[row.dueStatus]} />
                                        </TableCell>
                                        <TableCell data-testid={`lc-position-mismatched-${row.lcId}`}>
                                            {row.mismatchedCurrencyAmounts.length > 0
                                                ? formatMismatchedCurrencyAmounts(row.mismatchedCurrencyAmounts)
                                                : dash(null)}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </>
            )}

            {!loading && !report && (
                <Typography color="text.secondary">No data available.</Typography>
            )}
        </Box>
    );
};

export default LcPositionReport;
